import { useParams, useNavigate } from 'react-router-dom';
import { useProducer } from '@/hooks/useProducer';
import { useProducerTeam } from '@/hooks/useProducerTeam';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Mail, ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { ROLE_LABELS, type ProducerInvitation } from '@/types/team';
import { InviteMemberSheet } from '@/components/team/InviteMemberSheet';

export default function ProducerTeamInvitationsPage() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { invitations, loading: teamLoading, resendInvite, revokeInvite } = useProducerTeam(farmId);
  
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<ProducerInvitation | null>(null);

  if (!farmId) return null;

  const isLoading = producerLoading || teamLoading;

  const handleResend = async (invitation: ProducerInvitation) => {
    try {
      await resendInvite(invitation.id);
      // Toast is shown by the hook
    } catch {
      // Error toast is shown by the hook
    }
  };

  const handleRevokeClick = (invitation: ProducerInvitation) => {
    setInvitationToRevoke(invitation);
    setRevokeDialogOpen(true);
  };

  const handleConfirmRevoke = async () => {
    if (!invitationToRevoke) return;
    try {
      await revokeInvite(invitationToRevoke.id);
      // Toast is shown by the hook
    } catch {
      // Error toast is shown by the hook
    } finally {
      setRevokeDialogOpen(false);
      setInvitationToRevoke(null);
    }
  };

  const getStatusBadgeVariant = (status: ProducerInvitation['status']) => {
    switch (status) {
      case 'invited': return 'secondary';
      case 'expired': return 'outline';
    }
  };

  const getStatusLabel = (status: ProducerInvitation['status']) => {
    switch (status) {
      case 'invited': return 'Väntande';
      case 'expired': return 'Utgången';
    }
  };

  return (
    <ProducerLayout
      farmId={farmId}
      producerName={producer?.name}
      title="Inbjudningar"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 -ml-2"
              onClick={() => navigate(`/producer/farm/${farmId}/team`)}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Tillbaka
            </Button>
            <h1 className="text-2xl font-bold">Inbjudningar</h1>
            <p className="text-muted-foreground mt-1">
              Hantera väntande och utgångna inbjudningar till gården.
            </p>
          </div>
          <Button onClick={() => setInviteSheetOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Bjud in person
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-48" />
        ) : invitations.length === 0 ? (
          /* Empty state */
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Mail className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-1">Inga inbjudningar</h3>
              <p className="text-muted-foreground text-center mb-4 max-w-sm">
                Du har inga väntande eller utgångna inbjudningar just nu.
              </p>
              <Button onClick={() => setInviteSheetOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Bjud in person
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-post</TableHead>
                  <TableHead>Roll</TableHead>
                  <TableHead>Skickad</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Åtgärder</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{ROLE_LABELS[invitation.role]}</Badge>
                    </TableCell>
                    <TableCell>
                      {format(new Date(invitation.invitedAt), 'd MMM yyyy', { locale: sv })}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(invitation.status)}>
                        {getStatusLabel(invitation.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {invitation.status === 'invited' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResend(invitation)}
                          >
                            Påminn
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeClick(invitation)}
                          >
                            Återkalla
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResend(invitation)}
                          >
                            Skicka igen
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevokeClick(invitation)}
                          >
                            Ta bort
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Invite Sheet */}
      <InviteMemberSheet
        open={inviteSheetOpen}
        onOpenChange={setInviteSheetOpen}
        producerId={farmId}
      />

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Återkalla inbjudan</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill återkalla inbjudan till {invitationToRevoke?.email}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRevoke}>
              Återkalla
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ProducerLayout>
  );
}
